/**
 *
 * Aws On-Boarding
 *
 */

import assert from 'assert';

import React, { memo, useCallback, useEffect, useReducer } from 'react';

import { CloudAccountCreate, CloudProviders } from '@ariksa/cloud-account';
import {
  Box,
  Flex,
  HStack,
  RadioGroup,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import { Api } from 'api/index';
import {
  AwsIcon,
  errorToast,
  PageHeaderWithIcon,
  PermissionsActionsIcon,
} from 'app/components';
import { Form, FormSchema } from 'app/components/DataEntry/Form';
import { Modal } from 'app/components/Overlay/Modal';
import { OutlinedIconButton } from 'components/DataEntry/Button/OutlinedIconButton';
import { selectApp } from 'containers/App/selectors';
import { actions as appActions } from 'containers/App/slice';
import { helpHeaderMapping } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/OnboardAws/Help/utils';
import { OnboardingHelp } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/OnboardAws/types';
import { ReadOnlyTag } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/ReadOnlyTag';
import {
  renderRadioButton,
  stopSignWithMessage,
} from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { cloudAccountWizardSaga } from 'containers/Setup/CloudAccounts/CloudAccountWizard/saga';
import { radioBoxStyles } from 'containers/Setup/CloudAccounts/CloudAccountWizard/styles';
import { TemplateType } from 'containers/Setup/CloudAccounts/CloudAccountWizard/types';
import { useSearchParams } from 'hooks/useSearchParams';
import { useInjector } from 'utils/inject';

import { selectCloudAccountWizard } from '../../selectors';
import { actions, reducer, sliceKey } from '../../slice';
import { accountInformation } from '../../utils';

interface Props {
  onOpenProgress: () => void;
}

export const OnboardAWSForm = memo((props: Props) => {
  useInjector(sliceKey, reducer, cloudAccountWizardSaga);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isOpen, onClose } = useDisclosure();

  const { config, onboardingAccount, templateType } = useSelector(
    selectCloudAccountWizard,
  );
  const { cloudAccounts } = useSelector(selectApp);

  const location = useLocation();
  const memberAccount = location.state?.state;
  const { member_account: isMemberAccount } = useSearchParams();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      name: '',
      defaultName: '',
      accountNameUsedError: '',
      isAutomatic: true,
      roleArn: '',
      externalId: '',
      isConfirm: false,
      showHelp: OnboardingHelp.None,
      hasError: false,
      buckets: [],
    },
  );

  useEffect(() => {
    dispatch(actions.resetOnboardingState());
  }, [dispatch]);

  //1. get templates
  //2. get account config
  //3. onboard using external id (along with other params) from config
  //4. redirect to aws page

  const toggleHelp = useCallback(
    (helpType: OnboardingHelp) => {
      if (state.showHelp === helpType) {
        updateState({ showHelp: OnboardingHelp.None });
      } else {
        updateState({ showHelp: helpType });
      }
    },
    [state.showHelp],
  );

  // get account config
  useEffect(() => {
    dispatch(
      actions.getAccountConfig({
        q: {
          cloudType: CloudProviders.Aws,
        },
      }),
    );
  }, [templateType, dispatch, isMemberAccount]);

  //set default name
  useEffect(() => {
    if (isMemberAccount) {
      const name = memberAccount?.name ?? '';
      updateState({ defaultName: name });
      if (!name?.match(/^[a-zA-Z0-9-]*$/i))
        updateState({
          accountNameUsedError: 'Only Alphanumeric, hyphen is allowed!',
        });
    }
  }, [isMemberAccount, memberAccount?.name]);

  const handleSubmit = data => {
    const { account_name, role_arn, external_id } = data;

    assert(
      !!config.data?.aws_config?.external_id,
      'External id is not available',
    );

    const onSuccess = createAccount => {
      if (state.isAutomatic) tryOnboard(createAccount!, account_name);
      else navigate('/setup/accounts');
    };

    const onError = err => {
      if (err?.code === 409) {
        updateState({ accountNameUsedError: err.description ?? '-' });
      } else {
        errorToast({ title: err.title, description: err.description });
      }
    };

    const awsConfig = {
      external_id: state.isAutomatic
        ? config.data?.aws_config?.external_id!
        : external_id,
      onboard_member_account: false,
      role_arn: state.isAutomatic ? '' : role_arn,
    };

    if (isMemberAccount) {
      dispatch(
        actions.onboardCloudMemberAccount({
          q: {
            uuid: memberAccount.uuid,
            memberAccountOnboardRequest: {
              name: account_name,
              aws: awsConfig,
            },
          },
          onSuccess,
          onError,
        }),
      );
    } else {
      dispatch(
        actions.onboardCloudAccount({
          q: {
            cloudAccountCreateRequest: {
              name: account_name,
              cloud_type: CloudProviders.Aws,
              aws: awsConfig,
            },
          },
          onSuccess,
          onError,
        }),
      );
    }
  };

  const tryOnboard = (createAccount: CloudAccountCreate, accountName) => {
    props.onOpenProgress();
    const params_S3BucketForCloudtrail = state.buckets.length
      ? state.buckets
          ?.map(b => `arn:aws:s3:::${b},arn:aws:s3:::${b}/*`)
          .join(',')
      : '*';
    const params: any = {
      stackName: `Ariksa-Onboarding-${uuidv4()}`,
      param_ExternalID: createAccount.aws?.external_id,
      templateURL: config.data.aws_config?.cfs_template_url,
      param_AriksaAdminAccountId: config.data.aws_config?.account_id,
      param_S3GetObjectBuckets:
        templateType === TemplateType.AnalyzeS3
          ? params_S3BucketForCloudtrail
          : '',
    };

    const redirectUrl = Api.awsOnboardRedirectURL(params);
    window.open(redirectUrl, '_blank');
  };

  const handleReset = () => {
    navigate('/setup/accounts');
    if (isEmpty(cloudAccounts.data))
      dispatch(appActions.updateShowGettingStarted(true));
  };

  const errorMessage: FormSchema = config.isError
    ? {
        errorMessage: {
          type: 'custom',
          component: () => (
            <Flex color="red.300" fontSize="md">
              We can not process this request at this time. Please contact
              admin!
            </Flex>
          ),
        },
      }
    : {};

  const schemaForm = yup.object().shape({
    account_name: yup
      .string()
      .matches(/^[a-zA-Z0-9-_]*$/i, 'Only Alphanumeric, hyphen is allowed!'),
  });

  const roleArnFields: FormSchema =
    !state.isAutomatic && state.isConfirm
      ? {
          role_arn: {
            label: 'ARN of the IAM Role for Ariksa',
            type: 'text',
            placeholder: 'Enter role ARN',
            isDisabled: state.isAutomatic,
            isRequired: true,
            onChange: v => updateState({ roleArn: v }),
            value: state.roleArn,
            /*helpTooltip:
              'AWS Resource Name (ARN) for the IAM role that Ariksa needs to gather information regarding your cloud account',
            helpIcon: <QuestionCircleIcon />,*/
          },
          external_id: {
            label: 'External ID for the IAM Role',
            type: 'text',
            placeholder: 'Enter external ID',
            isDisabled: state.isAutomatic,
            isRequired: true,
            onChange: v => updateState({ externalId: v }),
            value: state.externalId,
          },
        }
      : {};

  return (
    <Stack>
      <Form
        title={
          <HStack w="full" justify="space-between">
            <Box>Add AWS Account</Box>
            <OutlinedIconButton
              leftIcon={<PermissionsActionsIcon p={1} />}
              onClick={() =>
                updateState({ showHelp: OnboardingHelp.Permission })
              }
            >
              Show Permissions
            </OutlinedIconButton>
          </HStack>
        }
        titleIcon={<AwsIcon />}
        isTitleIconFilled={false}
        //isLoading={templates.isLoading}
        formValidation={schemaForm}
        schema={{
          account_name: {
            type: 'text',
            label: 'Connector name',
            isRequired: true,
            tooltip: 'Only Alphanumeric, hyphen is allowed.',
            error: state.accountNameUsedError,
            /*onKeyDown: value => {
              setAccountNameUsedError('');
            },*/
            autoFocus: true,
            value: state.name,
            defaultValue: state.defaultName,
            onChange: value => {
              updateState({ name: value });
              if (value.match(/^[a-zA-Z0-9-]*$/i))
                updateState({ accountNameUsedError: '' });
              else
                updateState({
                  accountNameUsedError: 'Only Alphanumeric, hyphen is allowed!',
                });
            },
          },
          cfs_template_type_info: {
            type: 'custom-with-form-control',
            label: 'Onboarding mode',
            component: <ReadOnlyTag />,
          },
          role_arn_creation: {
            type: 'custom-with-form-control',
            label: 'How do you want to onboard AWS account(s)',
            component: (
              <HStack {...radioBoxStyles}>
                <RadioGroup
                  value={state.isAutomatic ? 'Automatic' : 'Manual'}
                  onChange={value =>
                    updateState({ isAutomatic: value === 'Automatic' })
                  }
                >
                  <HStack spacing={10}>
                    {renderRadioButton(
                      'Automatically',
                      'Onboards member accounts if applicable',
                      'Automatic',
                    )}
                    {renderRadioButton(
                      'Manually',
                      'I’ll create an IAM role for Ariksa',
                      'Manual',
                    )}
                  </HStack>
                </RadioGroup>
              </HStack>
            ),
          },
          confirmMessage: {
            type: 'custom',
            component: () =>
              stopSignWithMessage(
                'Review requirements before proceeding further',
                state.isAutomatic
                  ? 'Confirm that you have the right configuration for automatic onboarding'
                  : 'I have created a cross-account IAM role for manual onboarding',
                state.isConfirm,
                val => updateState({ isConfirm: val }),
                () =>
                  toggleHelp(
                    state.isAutomatic
                      ? OnboardingHelp.AutomatedOnboarding
                      : OnboardingHelp.ManualOnboarding,
                  ),
              ),
          },
          ...roleArnFields,
          ...errorMessage,
        }}
        helpDrawer={{
          isOpen: state.showHelp !== OnboardingHelp.None,
          onClose: () => updateState({ showHelp: OnboardingHelp.None }),
          content: helpHeaderMapping(state.showHelp)?.content,
          header: (
            <PageHeaderWithIcon
              label={helpHeaderMapping(state.showHelp)?.header}
              icon={helpHeaderMapping(state.showHelp)?.icon}
              reversed
            />
          ),
          styles: { body: { pt: 6 } },
        }}
        formOptions={{ defaultValues: { onboard_member_account: true } }}
        buttonOptions={{
          submit: {
            name: 'CONNECT',
            //<Tooltip label="We can not process this request at this time. Please contact admin!">
            isDisabled:
              !state.isConfirm ||
              !state.name ||
              config.isLoading ||
              onboardingAccount.isLoading ||
              !!state.accountNameUsedError ||
              (!state.isAutomatic && !state.roleArn && !state.externalId),
            isLoading: onboardingAccount.isLoading,
          },
          reset: {
            isVisible: true,
            onClick: handleReset,
          },
        }}
        styles={{
          form: { formWidth: '600px', title: { labelStyles: { w: 'full' } } },
        }}
        handleSubmit={handleSubmit}
      />

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        body={
          <Stack spacing={6}>
            {accountInformation(
              'Account ID',
              config.data.aws_config?.account_id,
            )}
            {accountInformation(
              'External ID',
              config.data.aws_config?.external_id,
            )}
          </Stack>
        }
      />
    </Stack>
  );
});
